import { onMounted, watch, computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { usePreferenceStore } from '@/stores/PreferenceStore'
import { Currency } from '@/stores/PreferenceStore'
import { PageProps as InertiaPageProps } from '@inertiajs/core'

interface AuthUser {
    can_checkout_with_euro?: boolean
    can_checkout_with_gbp?: boolean
}

interface PageProps extends InertiaPageProps {
    auth?: {
        user?: AuthUser
    }
}

export function useCurrency() {
    const preferences = usePreferenceStore()
    const page = usePage<PageProps>()

    // Function to check if user can use EUR
    const canUseEur = computed(() => {
        const user = page.props.auth?.user
        return user?.can_checkout_with_euro ?? false
    })

    // Function to check if user can use GBP
    const canUseGbp = computed(() => {
        const user = page.props.auth?.user
        return user?.can_checkout_with_gbp ?? true // Default to true if not specified
    })

    // Function to validate and set appropriate currency
    const validateAndSetCurrency = () => {
        const user = page.props.auth?.user
        if (!user) {
            // If no user, allow any currency (use persisted value)
            return
        }
        // If user can't use EUR and current currency is EUR, force GBP
        if (!canUseEur.value && preferences.currency === 'EUR') {
            preferences.forceSetCurrency('GBP')
        }
        
        // If user can't use GBP and current currency is GBP, force EUR
        if (!canUseGbp.value && preferences.currency === 'GBP') {
            preferences.forceSetCurrency('EUR')
        }
    }

    // Watch for auth changes
    watch(() => page.props.auth?.user, () => {
        validateAndSetCurrency()
    }, { immediate: true })

    // Run on mount
    onMounted(() => {
        validateAndSetCurrency()
    })

    return {
        canUseEur,
        canUseGbp,
        validateAndSetCurrency
    }
} 
